import React, { useContext } from "react";
import { t } from "ttag";

import type { ImageChooserBlock as ImageChooserBlockValue } from "@reactivated";

import { ImageChooserBlock } from "@thelabnyc/thelabui/src/components/ImageChooserBlock";
import { concatClassNames } from "@thelabnyc/thelabui/src/utils/styles";

import { ReactivatedContext } from "../../../context";
import { buildLocalImage } from "../../../utils/buildLocalImage";
import { Clickable } from "../../Clickables";
import type { ScreenOption } from "../engine";
import type { EngineImage } from "../engine/schema";

import themeStyles from "../../../styles/themes-2026.module.scss";
import styles from "./OptionTile.module.scss";

export type OptionTileProps = {
    option: ScreenOption;
    onClick: () => void;
    /** Renders the "See More Details" link when provided. */
    onShowDetails?: () => void;
};

/**
 * Resolve an `EngineImage` descriptor to an `ImageChooserBlock`;
 * pass catalog images through unchanged.
 */
const resolveEngineImage = (
    img: ImageChooserBlockValue | EngineImage,
    staticUrl: string,
): ImageChooserBlockValue =>
    "renditions" in img
        ? img
        : buildLocalImage(`${staticUrl}suite-builder/${img.src}`, {
              alt: "",
              width: img.width,
              height: img.height,
              mimetype: "image/webp",
          });

/**
 * Format a tile's price by category:
 *   - Accessory tile (price + isAccessory): `+ $X` additive prefix
 *   - Grouping tile (price, no SKU): "Starting at $X" derived min
 *   - Direct-SKU tile (price + sku): exact price
 *   - No price (undefined): no row rendered
 */
export const formatOptionPrice = (option: ScreenOption): string | null => {
    if (option.price === undefined) return null;
    const formatted = option.price.formatted;
    if (option.isAccessory) return t`+ ${formatted}`;
    if (!option.sku) return t`Starting at ${formatted}`;
    return formatted;
};

/** Denser content gets a smaller image so the tile reads balanced. */
const pickPictureClass = (option: ScreenOption): string => {
    if (option.imageVariant === "full") return styles.pictureFull;
    if (option.sku || option.description) return styles.pictureSmall;
    if (option.price !== undefined) return styles.pictureMedium;
    return styles.pictureLarge;
};

export const OptionTile = ({
    option,
    onClick,
    onShowDetails,
}: OptionTileProps) => {
    const { STATIC_URL } = useContext(ReactivatedContext);
    const priceLabel = formatOptionPrice(option);
    const pictureClass = pickPictureClass(option);
    const image = resolveEngineImage(option.image, STATIC_URL);

    return (
        <div
            className={concatClassNames([
                option.isSelected ? styles.tileSelected : styles.tile,
                themeStyles.offWhite,
            ])}
            // Clicking anywhere other than the actual clickables acts as though
            // the label was clicked
            onClick={() => onClick()}
        >
            <ImageChooserBlock
                value={image}
                attrs={{ className: pictureClass }}
                imageAttrs={{ className: styles.image }}
            />
            <div className={styles.content}>
                {option.sku && <span className={styles.sku}>{option.sku}</span>}
                <Clickable
                    className={styles.label}
                    onPress={onClick}
                    aria-pressed={option.isSelected}
                >
                    {option.label}
                </Clickable>
                {option.description && (
                    <span className={styles.description}>
                        {option.description}
                    </span>
                )}
                {priceLabel && (
                    <span className={styles.price}>{priceLabel}</span>
                )}
                {onShowDetails && (
                    <Clickable
                        className={styles.detailsLink}
                        onPress={onShowDetails}
                    >
                        {t`See More Details`}
                    </Clickable>
                )}
            </div>
        </div>
    );
};
